"use client";

import React, { useCallback, useMemo, useEffect } from "react";
import { Icon } from "@focal/ui-kit";
import { Button } from "@focal/ui-kit";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@focal/ui-kit";
import { Popover, PopoverContent, PopoverTrigger } from "@focal/ui-kit";
import { Tooltip, TooltipContent, TooltipTrigger } from "@focal/ui-kit";
import { LoadingSpinner } from "@focal/ui-kit";
import { Text } from "@focal/ui-kit";
import { cn } from "@/lib/utils";
import { CalendarOffIcon } from "./CalendarOffIcon";
import { Meeting, MeetingType, Participant } from "@/types";
import { format, isToday, startOfDay } from "date-fns";
import {
  useDebounce,
  useHasCalendarConnected,
  useSelectableMeetings,
} from "@/hooks";
import { useQuery } from "@tanstack/react-query";
import { getMeeting, MEETING_GET } from "@/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@focal/ui-kit";

export interface CalendarEventSelectorProps {
  /**
   * Tri-state selection value:
   * - `null` = no selection (the user hasn't chosen anything yet)
   * - `""` = explicit "No calendar event" selection (passes required validation)
   * - `string` (non-empty) = the selected meeting id
   */
  selectedMeetingId: string | null;
  /**
   * Fired when the user picks an item. The first argument follows the same
   * tri-state model as `selectedMeetingId`: `null` for "cleared", `""` for
   * the "No calendar event" item, or a meeting id string.
   */
  onMeetingSelected: (meetingId: string | null, meeting?: Meeting) => void;
  disabled?: boolean;
  /** Optional label text. Defaults to "Select calendar event" */
  label?: string;
  /** When true, shows confirmation dialog before changing meeting */
  isRecording?: boolean;
  /**
   * When true, this field is treated as required. The clear-X affordance is
   * hidden because clearing back to `null` would just put the user in an
   * invalid state — users must explicitly pick a meeting or "No calendar
   * event" to satisfy validation.
   */
  required?: boolean;
  /**
   * When true, renders the trigger with an error-state border and shows the
   * "Select a calendar event" message beneath it.
   */
  error?: boolean;
  /**
   * Live participant list for the selected-event tooltip, so edits made on
   * the page stay in sync. When undefined, the tooltip falls back to the
   * invitees on the meeting itself.
   */
  participants?: Array<Pick<Participant, "name" | "email">>;
  /**
   * Opt-in auto-selection (FE-972). When set and no selection exists yet,
   * the single in-progress event whose type matches is selected automatically
   * once today's meetings first load: "video" requires an ONLINE event,
   * "inPerson" requires a non-ONLINE event. Evaluated at most once per mount.
   */
  autoSelectType?: "video" | "inPerson";
}

/** cmdk value of the always-present "No calendar event" item. */
const NO_EVENT_VALUE = "no-calendar-event";

/** How far back the past-event list and its search reach. */
const PAST_LOOKBACK_DAYS = 14;
const PAST_GROUP_HEADING = "Past 2 weeks";

const formatMeetingTime = (meeting: Meeting): string => {
  const start = new Date(meeting.startAt);
  const timeRange = `${format(start, "h:mm a")} - ${format(
    new Date(meeting.endAt),
    "h:mm a",
  )}`;
  // A time range alone is ambiguous once the list spans more than one day.
  return isToday(start)
    ? timeRange
    : `${format(start, "MMM d")} · ${timeRange}`;
};

const isMeetingInProgress = (meeting: Meeting): boolean => {
  const now = Date.now();
  return meeting.startAt <= now && meeting.endAt >= now;
};

interface MeetingItemProps {
  meeting: Meeting;
  isSelected: boolean;
  isSuggested: boolean;
  onSelect: () => void;
}

const MeetingItem: React.FC<MeetingItemProps> = ({
  meeting,
  isSelected,
  isSuggested,
  onSelect,
}) => {
  const isInProgress = isMeetingInProgress(meeting);

  return (
    <CommandItem
      value={meeting.id}
      onSelect={onSelect}
      className={cn(
        "cursor-pointer overflow-hidden",
        isSuggested && "bg-primary/5 border-l-2 border-l-primary",
      )}
      data-cy={`meeting-option-${meeting.id}`}
    >
      <Icon
        name="CheckIcon"
        size="sm"
        className={cn(
          "mr-2 shrink-0",
          isSelected ? "opacity-100" : "opacity-0",
        )}
      />
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <span className="truncate font-medium block">
          {meeting.title || "Untitled meeting"}
        </span>
        <span className="text-xs text-muted-foreground truncate block">
          {formatMeetingTime(meeting)}
          {isInProgress && (
            <span className="ml-2 text-success font-medium">In progress</span>
          )}
        </span>
      </div>
      {isSuggested && (
        <Icon
          name="AutoAwesomeIcon"
          size="sm"
          className="shrink-0 ml-2 text-primary"
        />
      )}
    </CommandItem>
  );
};

export const CalendarEventSelector: React.FC<CalendarEventSelectorProps> = ({
  selectedMeetingId,
  onMeetingSelected,
  disabled = false,
  label = "Select calendar event",
  isRecording = false,
  required = false,
  error = false,
  participants,
  autoSelectType,
}) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [highlightedValue, setHighlightedValue] =
    React.useState(NO_EVENT_VALUE);
  const debouncedSearch = useDebounce(search, 300);
  const listRef = React.useRef<HTMLDivElement>(null);
  const autoSelectEvaluatedRef = React.useRef(false);

  // State for confirmation dialog during recording
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
  const [pendingSelection, setPendingSelection] = React.useState<{
    meetingId: string | null;
    meeting?: Meeting;
  } | null>(null);

  // Handle meeting selection with confirmation when recording
  const handleMeetingSelect = useCallback(
    (meetingId: string | null, meeting?: Meeting) => {
      if (isRecording && selectedMeetingId !== meetingId) {
        // Show confirmation dialog
        setPendingSelection({ meetingId, meeting });
        setShowConfirmDialog(true);
      } else {
        onMeetingSelected(meetingId, meeting);
        setOpen(false);
      }
    },
    [isRecording, selectedMeetingId, onMeetingSelected],
  );

  const handleConfirmChange = useCallback(() => {
    if (pendingSelection) {
      onMeetingSelected(pendingSelection.meetingId, pendingSelection.meeting);
      setPendingSelection(null);
      setShowConfirmDialog(false);
      setOpen(false);
    }
  }, [pendingSelection, onMeetingSelected]);

  const handleCancelChange = useCallback(() => {
    setPendingSelection(null);
    setShowConfirmDialog(false);
  }, []);

  const { hasCalendar, isLoading: isLoadingCalendar } =
    useHasCalendarConnected();
  const {
    meetings,
    loadMore,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading: isLoadingMeetings,
    isFetched: isMeetingsFetched,
    isError,
    refetch,
  } = useSelectableMeetings(hasCalendar, {
    lookbackDays: PAST_LOOKBACK_DAYS,
    search: debouncedSearch,
  });

  // A filter left over from the last time the list was open would silently
  // hide events the next time it opens.
  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  // Check if selected meeting is in today's list
  const selectedMeetingInList = useMemo(
    () => meetings.find((m) => m.id === selectedMeetingId),
    [meetings, selectedMeetingId],
  );

  // Fetch selected meeting by ID if not in today's list
  const { data: fetchedSelectedMeeting, isLoading: isLoadingSelectedMeeting } =
    useQuery({
      queryKey: [MEETING_GET, "selected-meeting", selectedMeetingId],
      queryFn: () => getMeeting(selectedMeetingId, { includeInvitees: true }),
      enabled:
        hasCalendar &&
        !!selectedMeetingId &&
        !isLoadingMeetings &&
        !selectedMeetingInList,
      staleTime: 5 * 60 * 1000, // 5 minutes
    });

  // Refetch meetings when dropdown opens
  useEffect(() => {
    if (open && hasCalendar) {
      refetch();
    }
  }, [open, hasCalendar, refetch]);

  // FE-972: one-shot auto-select of the single in-progress event matching the
  // surface type. Ref (not state) — later refetches must never re-trigger it.
  useEffect(() => {
    if (!autoSelectType || autoSelectEvaluatedRef.current) return;
    if (selectedMeetingId !== null) {
      // Pre-existing selection (URL param, restored draft) wins permanently.
      autoSelectEvaluatedRef.current = true;
      return;
    }
    // A filtered list is not the full set of in-progress events, so it can't
    // answer "is there exactly one?".
    if (debouncedSearch) return;
    if (!hasCalendar || !isMeetingsFetched) return;
    // Consume the one evaluation now: if the field is busy when data first
    // arrives, forfeit rather than fire late once disabled/recording flip back.
    autoSelectEvaluatedRef.current = true;
    if (disabled || isRecording) return;

    const inProgress = meetings.filter(isMeetingInProgress);
    if (inProgress.length !== 1) return;
    const candidate = inProgress[0];
    const matchesType =
      autoSelectType === "video"
        ? candidate.meetingType === MeetingType.ONLINE
        : candidate.meetingType !== MeetingType.ONLINE;
    if (matchesType) {
      onMeetingSelected(candidate.id, candidate);
    }
  }, [
    autoSelectType,
    disabled,
    isRecording,
    selectedMeetingId,
    hasCalendar,
    isMeetingsFetched,
    meetings,
    onMeetingSelected,
    debouncedSearch,
  ]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      const isAtBottom =
        target.scrollHeight - target.scrollTop <= target.clientHeight + 50;

      if (isAtBottom && hasNextPage && !isFetchingNextPage) {
        loadMore();
      }
    },
    [hasNextPage, isFetchingNextPage, loadMore],
  );

  // Separate suggested (in-progress) meetings from other meetings
  // Include fetched meeting from another day in suggested section
  const { suggestedMeetings, otherMeetings, pastMeetings } = useMemo(() => {
    const suggested: Meeting[] = [];
    const others: Meeting[] = [];
    const past: Meeting[] = [];
    const todayStart = startOfDay(new Date()).getTime();

    // Add fetched meeting from another day to suggested list
    if (fetchedSelectedMeeting && !selectedMeetingInList) {
      suggested.push(fetchedSelectedMeeting);
    }

    meetings.forEach((meeting) => {
      if (isMeetingInProgress(meeting)) {
        suggested.push(meeting);
      } else if (meeting.startAt < todayStart) {
        past.push(meeting);
      } else {
        others.push(meeting);
      }
    });

    return {
      suggestedMeetings: suggested,
      otherMeetings: others,
      pastMeetings: past,
    };
  }, [meetings, fetchedSelectedMeeting, selectedMeetingInList]);

  // Use meeting from list or fetched meeting
  const selectedMeeting = useMemo(
    () => selectedMeetingInList ?? fetchedSelectedMeeting,
    [selectedMeetingInList, fetchedSelectedMeeting],
  );

  // cmdk highlights its first item, which is now permanently "No calendar
  // event" — Enter after a search would throw the search away. Point the
  // highlight at the first result instead.
  const firstResultId =
    suggestedMeetings[0]?.id ?? otherMeetings[0]?.id ?? pastMeetings[0]?.id;
  useEffect(() => {
    setHighlightedValue(
      debouncedSearch && firstResultId ? firstResultId : NO_EVENT_VALUE,
    );
  }, [debouncedSearch, firstResultId]);

  const emptyMessage = useMemo(
    () =>
      debouncedSearch
        ? "No meetings match that search."
        : `No meetings found in the ${PAST_GROUP_HEADING.toLowerCase()}.`,
    [debouncedSearch],
  );

  const isLoadingSelectedMeetingDisplay =
    !!selectedMeetingId && !selectedMeeting && isLoadingSelectedMeeting;

  const tooltipParticipants = useMemo(() => {
    const people = participants ?? selectedMeeting?.invitees ?? [];
    return people
      .map((person) => person.name || person.email)
      .filter((displayName): displayName is string => !!displayName);
  }, [participants, selectedMeeting]);

  const displayValue = useMemo(() => {
    if (selectedMeetingId === null) {
      return label;
    }
    if (selectedMeetingId === "") {
      return "No calendar event";
    }
    // Title only — the time range (and invitees) show in the hover tooltip,
    // so long titles aren't squeezed into an ellipsis by the time suffix.
    if (selectedMeeting) {
      return selectedMeeting.title || "Untitled";
    }
    if (isLoadingSelectedMeetingDisplay) {
      return "Loading...";
    }
    // Meeting not found (invalid ID or deleted)
    return "Meeting not found";
  }, [
    selectedMeetingId,
    selectedMeeting,
    label,
    isLoadingSelectedMeetingDisplay,
  ]);

  // Don't render if no calendar connected
  if (isLoadingCalendar) {
    return null;
  }

  if (!hasCalendar) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2 w-full max-w-md">
      {error && (
        <Text
          variant="p3"
          textColor="error"
          data-cy="calendar-event-selector-error"
          className="text-right"
        >
          Calendar event selection required
        </Text>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        {/* Delay so a quick move-and-click doesn't flash the tooltip */}
        <Tooltip delayDuration={500}>
          <TooltipTrigger
            asChild
            // Radix opens tooltips on focus with no delay, and the popover
            // refocuses the trigger after picking an event — which popped the
            // tooltip uninvited. preventDefault suppresses the focus-open
            // path; hover still works.
            onFocus={(event) => event.preventDefault()}
          >
            <PopoverTrigger asChild>
              <Button
                id="calendar-event-selector"
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className={cn(
                  "w-full justify-between",
                  // Figma blues with no theme tokens yet — ui-kit follow-up
                  selectedMeetingId !== null
                    ? "bg-blue-50 border-border-blue text-blue-700 hover:bg-blue-50 hover:text-blue-700 font-normal"
                    : open &&
                        "bg-primary-foreground border-primary-outline-stroke",
                  error && "border-error",
                )}
                disabled={disabled}
                data-cy="calendar-event-selector"
              >
                <div className="flex items-center gap-2 truncate">
                  {selectedMeetingId === "" ? (
                    <CalendarOffIcon className="shrink-0 text-blue-700" />
                  ) : (
                    <Icon
                      name="CalendarIcon"
                      size="sm"
                      className={cn(
                        "shrink-0",
                        selectedMeetingId !== null
                          ? "text-blue-700"
                          : "opacity-50",
                      )}
                    />
                  )}
                  <span className="truncate">{displayValue}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {selectedMeetingId !== null && !disabled && !required && (
                    <Icon
                      name="CloseIcon"
                      size="sm"
                      className="opacity-50 hover:opacity-100 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMeetingSelect(null);
                      }}
                      data-cy="clear-calendar-event"
                    />
                  )}
                  <Icon
                    name="ChevronDownIcon"
                    size="sm"
                    className={cn(
                      selectedMeetingId !== null
                        ? "text-blue-700"
                        : "opacity-50",
                    )}
                  />
                </div>
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          {/* Only for a real selected meeting, and never over the open list */}
          {selectedMeeting && !open && (
            <TooltipContent side="bottom" className="max-w-xs text-left">
              {/* text-inherit keeps the tooltip's inverted foreground color */}
              <Text variant="p2" className="font-medium text-inherit">
                {selectedMeeting.title || "Untitled"} ·{" "}
                <Text
                  variant="p2"
                  as="span"
                  className="font-normal text-inherit"
                >
                  {formatMeetingTime(selectedMeeting)}
                </Text>
              </Text>
              {tooltipParticipants.length > 0 && (
                <ul className="mt-2 list-disc pl-4 space-y-0.5">
                  {tooltipParticipants.map((displayName, index) => (
                    <li key={`${displayName}-${index}`}>{displayName}</li>
                  ))}
                </ul>
              )}
            </TooltipContent>
          )}
        </Tooltip>
        {/* Unportaled popovers inherit text-center from AddMeetingLayout's card */}
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] max-h-[300px] p-0 overflow-hidden text-left">
          <Command
            shouldFilter={false}
            value={highlightedValue}
            onValueChange={setHighlightedValue}
            className="overflow-hidden"
          >
            <CommandInput
              placeholder="Search meetings..."
              value={search}
              onValueChange={setSearch}
              data-cy="calendar-event-search"
            />
            <CommandList
              ref={listRef}
              onScroll={handleScroll}
              className="overflow-y-auto overflow-x-hidden max-h-[250px]"
            >
              {/* Clear selection option */}
              <CommandGroup>
                <CommandItem
                  value={NO_EVENT_VALUE}
                  onSelect={() => handleMeetingSelect("")}
                  className="cursor-pointer"
                  data-cy="no-calendar-event-option"
                >
                  <Icon
                    name="CheckIcon"
                    size="sm"
                    className={cn(
                      "mr-2 shrink-0",
                      selectedMeetingId === "" ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
                    <span className="truncate font-medium block">
                      No calendar event
                    </span>
                    <span className="text-xs text-muted-foreground truncate block">
                      Select this option if no meeting is scheduled
                    </span>
                  </div>
                </CommandItem>
              </CommandGroup>

              <CommandSeparator />

              {/* Loading state */}
              {isLoadingMeetings && meetings.length === 0 && (
                <div className="flex justify-center items-center py-4">
                  <LoadingSpinner />
                </div>
              )}

              {/* Error state */}
              {isError && meetings.length === 0 && (
                <div className="flex flex-col items-center justify-center py-4 px-2 text-center">
                  <span className="text-sm text-destructive">
                    Failed to load meetings
                  </span>
                  <span className="text-xs text-muted-foreground mt-1">
                    Please try again later
                  </span>
                </div>
              )}

              {/* Empty state. Not CommandEmpty: with a permanently rendered
                  item, cmdk's match count never reaches zero. */}
              {!isLoadingMeetings && !isError && meetings.length === 0 && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {emptyMessage}
                </div>
              )}

              {/* Suggested meetings (in-progress) */}
              {suggestedMeetings.length > 0 && (
                <CommandGroup heading="Suggested">
                  {suggestedMeetings.map((meeting) => (
                    <MeetingItem
                      key={meeting.id}
                      meeting={meeting}
                      isSelected={selectedMeetingId === meeting.id}
                      isSuggested={true}
                      onSelect={() => handleMeetingSelect(meeting.id, meeting)}
                    />
                  ))}
                </CommandGroup>
              )}

              {/* Other meetings */}
              {otherMeetings.length > 0 && (
                <CommandGroup heading="Today">
                  {otherMeetings.map((meeting) => (
                    <MeetingItem
                      key={meeting.id}
                      meeting={meeting}
                      isSelected={selectedMeetingId === meeting.id}
                      isSuggested={false}
                      onSelect={() => handleMeetingSelect(meeting.id, meeting)}
                    />
                  ))}
                </CommandGroup>
              )}

              {/* Past meetings */}
              {pastMeetings.length > 0 && (
                <CommandGroup heading={PAST_GROUP_HEADING}>
                  {pastMeetings.map((meeting) => (
                    <MeetingItem
                      key={meeting.id}
                      meeting={meeting}
                      isSelected={selectedMeetingId === meeting.id}
                      isSuggested={false}
                      onSelect={() => handleMeetingSelect(meeting.id, meeting)}
                    />
                  ))}
                </CommandGroup>
              )}

              {/* Loading indicator for pagination */}
              {isFetchingNextPage && (
                <div className="flex justify-center items-center py-2">
                  <LoadingSpinner />
                </div>
              )}

              {/* End of list indicator */}
              {!hasNextPage && meetings.length > 0 && !isFetching && (
                <div className="text-center text-xs text-muted-foreground py-2">
                  No more meetings
                </div>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Confirmation dialog for changing meeting during recording */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change linked calendar event?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingSelection?.meetingId
                ? "This recording will be linked to a different calendar event. The meeting title and participants will be updated to match the new event."
                : "This recording will no longer be linked to a calendar event. The meeting title and participants will be cleared."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelChange} className="px-4">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmChange} className="px-4">
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
